import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
  CircularProgress,
  Alert,
  AlertTitle,
  Tooltip,
  IconButton,
  Paper,
  alpha,
  Fade,
} from "@mui/material";
import { listDetections, createDetection, updateDetection,deleteDetection } from "../../services/annotationApi";
import AuthService from "../../services/AuthService";
import { 
  TouchApp, 
  Check, 
  Cancel, 
  Edit, 
  ArrowBack, 
  FileUpload, 
  PhotoCamera,
  Close,
  Refresh
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_BASE_URL_ = process.env.REACT_APP_API_URL_;

interface Detection {
  id: string;
  label: string;
  confidence: number;
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
  detectionIndex: number;
  userId: string;
  imageFile: string;
}

interface LocalizationBox {
  startX: number;
  startY: number;
  width: number;
  height: number;
}

interface CaseDetectionPageProps {
  caseId: string;
  userId?: string;
  imageFilePath?: string;
}

const CaseDetection: React.FC<CaseDetectionPageProps> = ({ caseId, userId, imageFilePath }) => {
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [detection, setDetection] = useState<Detection | null>(null);
  const [imagePath, setImagePath] = useState(imageFilePath || "");
  const [imageUrl, setImageUrl] = useState(""); // URL for display
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const returnPath = queryParams.get("returnTo") || `/cases/${caseId}/details?tab=details`;

  // Manual localization states
  const [isLocalizing, setIsLocalizing] = useState(false);
  const [localizationBox, setLocalizationBox] = useState<LocalizationBox | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [originalImageDimensions, setOriginalImageDimensions] = useState({ width: 0, height: 0 });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [editingDetectionId, setEditingDetectionId] = useState<string | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  
  // New state for drag and drop
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  
  // New state for camera functionality
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraLoading, setCameraLoading] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [frontCamera, setFrontCamera] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [cameraInitAttempts, setCameraInitAttempts] = useState<number>(0);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState<number>(0);
  
  // Add refs for video and canvas elements
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
    
  const fetchDetection = async () => {
    console.log("Fetching detection for case:", caseId);
    if (!caseId) {
      console.warn("Cannot fetch detection: missing caseId");
      return;
    }
    
    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();

    if (!accessToken) {
      console.warn("Cannot fetch detection: missing access token");
      return;
    }
    
    try {
      // Get detections from the API
      const results = await listDetections(accessToken, caseId);
      console.log("Fetched detections (raw):", results);
      
      // Ensure we have an array to work with
      const detectionsArray = Array.isArray(results) ? results : [];
      console.log("Detections array:", detectionsArray);
      
      // Find any image upload detection first to get the image path
      const imageUpload = detectionsArray.find(d => d.label === "image_upload");
if (imageUpload && imageUpload.imageFile) {
  console.log("Found image file:", imageUpload.imageFile);
  if (imageUpload.imageFile.startsWith('http') || imageUpload.imageFile.startsWith('/')) {
    setImagePath(imageUpload.imageFile);
  } else {
    setImagePath(`/testing3/${imageUpload.imageFile}`);
  }
}
      
      // Check for manual detection - we only support one per image
      const manualDetection = detectionsArray.find(d => d.label === "manual_detection");
      
      if (manualDetection) {
        console.log("Found manual detection:", manualDetection);
        setDetection(manualDetection);
        setEditingDetectionId(manualDetection.id);
        
        // If we have an image displayed, set up the localization box from the existing detection
        if (imageContainerRef.current) {
          const imgElement = imageContainerRef.current.querySelector('img');
          if (imgElement && imgElement.complete) {
            const displayWidth = imgElement.width;
            const displayHeight = imgElement.height;
            
            console.log("Image dimensions for box calculation:", {
              displayWidth,
              displayHeight,
              detection: manualDetection
            });
            
            // Check for valid coordinates in the detection
            if (
              typeof manualDetection.x_min === 'number' && 
              typeof manualDetection.y_min === 'number' && 
              typeof manualDetection.x_max === 'number' && 
              typeof manualDetection.y_max === 'number'
            ) {
              // Since our coordinates are normalized (0-1), we just multiply to get pixel values
              const startX = Math.round(manualDetection.x_min * displayWidth);
              const startY = Math.round(manualDetection.y_min * displayHeight);
              const width = Math.round((manualDetection.x_max - manualDetection.x_min) * displayWidth);
              const height = Math.round((manualDetection.y_max - manualDetection.y_min) * displayHeight);
              
              console.log("Setting localization box from existing detection:", {
                startX, startY, width, height
              });
              
              // Set the localization box to match the existing detection
              setLocalizationBox({
                startX,
                startY,
                width,
                height
              });
            } else {
              console.warn("Detection has invalid coordinates:", manualDetection);
            }
          } else {
            console.log("Image not fully loaded yet, will set box on load event");
            // Image is not fully loaded yet, the onLoad handler will handle setting the box
          }
        }
      } else {
        // No manual detection yet
        setDetection(null);
        setEditingDetectionId(null);
        setLocalizationBox(null);
      }
    } catch (error) {
      console.error("Error fetching detection:", error);
    }
  };

  // Get userId from auth service if not provided via props
  useEffect(() => {
    const getUserId = async () => {
      if (!userId) {
        const authService = new AuthService();
        try {
          const user = await authService.getUser();
          const userIdFromProfile = user?.profile.sub || "";
          console.log("Got user ID from profile:", userIdFromProfile);
          setCurrentUserId(userIdFromProfile);
        } catch (error) {
          console.error("Failed to get user ID:", error);
        }
      } else {
        setCurrentUserId(userId);
      }
    };
    getUserId();
  }, [userId]);

  // Fetch detection when component loads or caseId changes
  useEffect(() => {
    console.log("CaseDetection component mounted/updated with caseId:", caseId);
    if (caseId) {
      setLoading(true);
      fetchDetection()
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]); // fetchDetection is intentionally omitted to prevent infinite loops

  // Load image path from detection if available
  useEffect(() => {
    if (detection && detection.imageFile && !imagePath) {
      console.log("Loading image path from detection:", detection.imageFile);
      
      // If the imageFile is a full path, use it directly
      // Otherwise, construct a path for the API
      if (detection.imageFile.startsWith('http') || detection.imageFile.startsWith('/')) {
        setImagePath(detection.imageFile);
      } else {
        setImagePath(`/testing3/${detection.imageFile}`);
      }
    }
  }, [detection, imagePath]);
  
  // Check if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      setIsMobile(
        /android|webos|iphone|ipad|ipod|blackberry|windows phone/.test(userAgent)
      );
    };
    
    checkMobile();
  }, []);
  
  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading && !imagePath) {
      setIsDragActive(true);
    }
  }, [loading, imagePath]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!loading && !imagePath) {
      setIsDragActive(true);
    }
  }, [loading, imagePath]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    if (!loading && !imagePath && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type.startsWith('image/')) {
        handleFileSelected(droppedFile);
      } else {
        setUploadError("Please upload an image file.");
      }
    }
  }, [loading, imagePath]);
  
  // Check and enumerate available cameras
  const enumerateCameras = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.log("enumerateDevices() not supported");
        return [];
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      console.log("Available cameras:", cameras);
      setAvailableCameras(cameras);
      return cameras;
    } catch (err) {
      console.error("Error enumerating devices:", err);
      return [];
    }
  };
  
  // Camera handlers
  const openCamera = async () => {
    try {
      setCameraLoading(true);
      setCameraError(null);
      setCameraOpen(true); // Open dialog immediately to show loading state

      // Get available cameras first
      const cameras = await enumerateCameras();
      
      // Determine which camera to use
      let constraints: MediaStreamConstraints = { audio: false, video: true };
      
      if (cameras.length > 0) {
        // If we have specific device IDs, use them
        const cameraToUse = cameras[currentCameraIndex];
        if (cameraToUse?.deviceId) {
          constraints = {
            audio: false,
            video: {
              deviceId: { exact: cameraToUse.deviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          };
          console.log(`Using camera ${currentCameraIndex + 1}/${cameras.length} with ID: ${cameraToUse.deviceId}`);
        }
      } else {
        // Fall back to facingMode if no device IDs are available
        const preferredFacingMode = isMobile ? "environment" : "user";
        constraints = {
          audio: false,
          video: {
            facingMode: frontCamera ? "user" : preferredFacingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        console.log("Using facingMode constraint:", constraints.video);
      }
      
      console.log(`Camera init attempt ${cameraInitAttempts + 1} with constraints:`, constraints);
      
      // For Safari compatibility, explicitly set timeouts
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Camera access timeout")), 10000);
      });
      
      // Try to get the media stream with timeout
      const stream = await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        timeoutPromise
      ]) as MediaStream;
      
      // If we reach here, we have a stream
      setCameraStream(stream);
      
      if (videoRef.current) {
        // In some browsers, setting srcObject can be asynchronous
        videoRef.current.srcObject = stream;
        
        // Force a reload of the video element
        videoRef.current.load();
        
        // Add event listeners to handle when video is ready
        videoRef.current.onloadedmetadata = () => {
          console.log("Video metadata loaded");
          if (videoRef.current) {
            videoRef.current.play()
              .then(() => {
                console.log("Video playback started");
                setCameraLoading(false);
                setCameraInitAttempts(0); // Reset attempts on success
              })
              .catch(err => {
                console.error("Error playing video:", err);
                setCameraError("Failed to start video playback. Please try Switch Camera.");
                setCameraLoading(false);
              });
          }
        };
        
        videoRef.current.onerror = () => {
          console.error("Video element error");
          setCameraError("Video element encountered an error. Please try Switch Camera.");
          setCameraLoading(false);
        };
        
        // Set a timeout in case the onloadedmetadata event never fires
        setTimeout(() => {
          if (cameraLoading) {
            console.log("Camera init timed out - attempting to force play");
            if (videoRef.current) {
              videoRef.current.play()
                .then(() => {
                  console.log("Forced video playback started");
                  setCameraLoading(false);
                })
                .catch(err => {
                  console.error("Forced play error:", err);
                  // Don't set error here as we'll try the fallback
                  tryFallbackInitialization();
                });
            }
          }
        }, 3000);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      let errorMessage = "Could not access camera. Please try the Switch Camera button or check permissions.";
      
      // More specific error messages based on the error
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = "Camera access was denied. Please allow camera permissions in your browser settings.";
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = "No camera found on your device or it's being used by another application.";
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage = "Camera is in use by another application or is not accessible.";
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = "Camera doesn't support the requested settings. Try the Switch Camera button.";
        } else if (err.name === 'AbortError') {
          errorMessage = "Camera access was aborted. Please try again or use Switch Camera.";
        }
      }
      
      setCameraError(errorMessage);
      setCameraLoading(false);
      
      // If this is the first attempt, try a fallback
      if (cameraInitAttempts < 2) {
        tryFallbackInitialization();
      }
    }
  };

  // Fallback camera initialization with simpler constraints
  const tryFallbackInitialization = () => {
    setCameraInitAttempts(prev => prev + 1);
    setTimeout(() => {
      console.log("Trying fallback camera initialization");
      // Toggle frontCamera which will trigger different constraints
      setFrontCamera(prev => !prev);
      // Re-attempt with new settings after a short delay
      setTimeout(() => {
        if (cameraOpen) {
          switchCamera();
        }
      }, 500);
    }, 1000);
  };

  const switchCamera = async () => {
    try {
      setCameraLoading(true);
      
      // Close existing stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      if (availableCameras.length > 1) {
        // If we have multiple cameras, cycle through them
        setCurrentCameraIndex(prevIndex => (prevIndex + 1) % availableCameras.length);
      } else {
        // If we don't have specific cameras enumerated, just toggle facingMode
        setFrontCamera(prev => !prev);
      }
      
      // Small delay before reopening camera with new settings
      setTimeout(() => {
        openCamera();
      }, 300);
      
    } catch (err) {
      console.error("Error switching camera:", err);
      setCameraError("Failed to switch camera. Please try again or use file upload instead.");
      setCameraLoading(false);
    }
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);
    setCameraLoading(false);
    setCameraError(null);
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert to file
        canvas.toBlob((blob) => {
          if (blob) {
            const fileName = `camera_capture_${Date.now()}.jpg`;
            const file = new File([blob], fileName, { type: 'image/jpeg' });
            
            // Process the captured file
            handleFileSelected(file);
            
            // Close camera
            closeCamera();
          }
        }, 'image/jpeg', 0.9); // Use 90% quality for JPEG
      }
    }
  };

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setSelectedFileName(file.name);
    
    // Create preview URL for the image
    const previewUrl = URL.createObjectURL(file);
    setImageUrl(previewUrl);
    setUploadSuccess(false);
    setUploadError("");
    
    // Update the file input for form submission if needed
    if (fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      handleFileSelected(selectedFile);
    } else {
      setSelectedFileName("");
      setImageUrl("");
      setSelectedFile(null);
    }
  };

  const handleUploadButtonClick = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file first");
      return;
    }
    
    if (!caseId) {
      setUploadError("No case ID provided. Cannot upload image.");
      return;
    }

    setUploading(true);
    setUploadError("");
    
    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();

    if (!accessToken) {
      setUploadError("Authentication error. Please sign in again.");
      setUploading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      const response = await fetch(`${API_BASE_URL}/annotations/uploadfile`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log("Upload result:", result);
      
      // Store both the server-side path and the display URL
      setImagePath(result.filePath);
      
      // Create an initial detection record to associate the image with the case
      try {
        await createDetection(accessToken, {
          userId: currentUserId,
          caseId: caseId,
          imageFile: result.filePath,
          label: "image_upload",  // Special label to indicate this is just the base image
          confidence: 1.0,
          x_min: 0,
          y_min: 0,
          x_max: 0,
          y_max: 0
        });
        
        console.log("Created initial detection record for uploaded image");
        await fetchDetection();
      } catch (detectionError) {
        console.error("Failed to create initial detection record:", detectionError);
      }
      
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 3000);

        // Clear image preview since we'll display it differently
        if (imageUrl) {
          URL.revokeObjectURL(imageUrl);
          setImageUrl(""); // Clear the imageUrl state
}    } catch (err) {
      console.error("Upload failed", err);
      setUploadError("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSelectedFile(null);
      setSelectedFileName("");
    }
  };

  const clearSelectedImage = () => {
    setSelectedFile(null);
    setSelectedFileName("");
    setImageUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetImage = async () => {
  if (!caseId) {
    console.warn("Cannot delete detection: missing caseId");
    return;
  }

  try {
    setLoading(true);
    
    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();
    
    if (!accessToken) {
      setUploadError("Authentication error. Please sign in again.");
      return;
    }
    
    // Delete all detections for this case
    const response = await deleteDetection(accessToken, caseId);
    console.log("Delete response:", response);
    
    // Reset all local state
    setImagePath("");
    setImageUrl("");
    setSelectedFile(null);
    setLocalizationBox(null);
    setIsLocalizing(false);
    setEditingDetectionId(null);
    setDetection(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    
    setUploadSuccess(true);
    setTimeout(() => setUploadSuccess(false), 3000);
    
  } catch (error: any) {
    console.error("Failed to delete detection:", error);
    let errorMessage = "Failed to delete image and detection. Please try again.";
      
    // Try to extract more specific error information if available
    if (error.response?.data?.detail) {
      errorMessage = `Error: ${error.response.data.detail}`;
    } else if (error.message) {
      errorMessage = `Error: ${error.message}`;
    }
    
    setUploadError(errorMessage);
  } finally {
    setLoading(false);
  }
};
  
  // Manual localization handlers
  const handleStartLocalizing = () => {
    console.log("Starting localization mode", editingDetectionId ? "for existing detection" : "for new detection");
    setIsLocalizing(true);
    setIsDrawing(false);
    // Only clear the box if we're not editing an existing detection
    // Otherwise keep the current box to show the existing selection
    if (!editingDetectionId) {
      setLocalizationBox(null);
    }
    setConfirmDialogOpen(false);
  };

  const handleCancelLocalizing = () => {
    console.log("Canceling localization");
    setIsLocalizing(false);
    setIsDrawing(false);
    // If canceling and we have an existing detection, restore its box from fetched data
    if (editingDetectionId) {
      fetchDetection();
    } else {
      setLocalizationBox(null);
    }
    setConfirmDialogOpen(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isLocalizing) return;
    e.preventDefault(); // Prevent default browser behavior
    
    // Get container position
    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    // Calculate position relative to the container
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    console.log("Mouse down at:", x, y);
    
    setIsDrawing(true);
    setLocalizationBox({
      startX: x,
      startY: y,
      width: 0,
      height: 0
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isLocalizing || !isDrawing || !localizationBox) return;
    e.preventDefault(); // Prevent default browser behavior
    
    // Get container position
    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    // Calculate position relative to the container
    const x = Math.min(Math.max(0, e.clientX - rect.left), rect.width);
    const y = Math.min(Math.max(0, e.clientY - rect.top), rect.height);
    
    // Update box in real-time
    setLocalizationBox(prev => {
      if (!prev) return null;
      return {
        startX: prev.startX,
        startY: prev.startY,
        width: x - prev.startX,
        height: y - prev.startY
      };
    });
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isLocalizing || !isDrawing) return;
    e.preventDefault(); // Prevent default browser behavior
    
    console.log("Mouse up, box:", localizationBox);
    setIsDrawing(false);
    
    // If box is too small, ignore it
    if (localizationBox && 
        (Math.abs(localizationBox.width) < 10 || Math.abs(localizationBox.height) < 10)) {
      console.log("Box too small, ignoring");
      setLocalizationBox(null);
      return;
    }
    
    // Open the confirmation dialog
    setConfirmDialogOpen(true);
  };

  const handleConfirmLocalization = async () => {
    if (!localizationBox || !imageContainerRef.current) {
      console.error("Missing localization box or image container reference");
      setUploadError("Missing localization data. Please try drawing the box again.");
      return;
    }
    
    const imgElement = imageContainerRef.current.querySelector('img');
    if (!imgElement) {
      console.error("Could not find image element in container");
      setUploadError("Image element not found. Please try refreshing the page.");
      return;
    }
    
    // Get the actual image dimensions (not the displayed size)
    const naturalWidth = originalImageDimensions.width || imgElement.naturalWidth;
    const naturalHeight = originalImageDimensions.height || imgElement.naturalHeight;
    
    // Get the displayed image dimensions
    const displayWidth = imgElement.width;
    const displayHeight = imgElement.height;
    
    console.log("Image dimensions:", { 
      naturalWidth, 
      naturalHeight, 
      displayWidth, 
      displayHeight,
      box: localizationBox
    });
    
    // Normalize the box (handle negative width/height)
    let startX = localizationBox.startX;
    let startY = localizationBox.startY;
    let width = localizationBox.width;
    let height = localizationBox.height;
    
    if (width < 0) {
      startX += width;
      width = Math.abs(width);
    }
    
    if (height < 0) {
      startY += height;
      height = Math.abs(height);
    }
    
    // Convert to coordinates in the original image and normalize to 0-1 range
    const x_min = Math.max(0, startX) / displayWidth;  
    const y_min = Math.max(0, startY) / displayHeight;
    const x_max = Math.min(displayWidth, startX + width) / displayWidth;
    const y_max = Math.min(displayHeight, startY + height) / displayHeight;
    
    console.log("Normalized coordinates:", { x_min, y_min, x_max, y_max });
    
    // Validate coordinates
    if (isNaN(x_min) || isNaN(y_min) || isNaN(x_max) || isNaN(y_max)) {
      console.error("Invalid coordinates calculated");
      setUploadError("Invalid coordinates calculated. Please try again.");
      setConfirmDialogOpen(false);
      return;
    }

    const authService = new AuthService();
    const accessToken = await authService.getAccessToken();

    if (!accessToken) {
      setUploadError("Authentication error. Please sign in again.");
      setConfirmDialogOpen(false);
      return;
    }
    
    // Extract filename from path
    const fileName = imagePath.includes('/') 
      ? imagePath.split('/').pop() || ""
      : imagePath;
      
    console.log("Using image file name:", fileName);
    
    try {
      setLoading(true);
      // Always use updateDetection to patch existing detection or create if none exists
      const localizationData = {
        label: "manual_detection",
        confidence: 1.0,
        x_min,
        y_min,
        x_max,
        y_max
      };
      
      console.log("Updating detection for case:", caseId);
      
      // Use the caseId to update the detection - this will create or update based on backend logic
      const response = await updateDetection(accessToken, caseId, localizationData);
      
      console.log("Detection updated successfully:", response);
      
      if (response && response.id) {
        setEditingDetectionId(response.id);
      } else {
        console.warn("Response didn't include a detection ID:", response);
      }
      
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
      
      // Refresh detection to get the latest data
      await fetchDetection();
      
      // Reset localization state
      setIsLocalizing(false);
      setConfirmDialogOpen(false);
    } catch (error: any) {
      console.error("Failed to save manual detection:", error);
      let errorMessage = "Failed to save detection. Please try again.";
      
      // Try to extract more specific error information if available
      if (error.response?.data?.detail) {
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setUploadError(errorMessage);
      setConfirmDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle image load to capture its original dimensions
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    console.log("Image loaded, natural dimensions:", img.naturalWidth, img.naturalHeight);
    setOriginalImageDimensions({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
    
    // When image loads, check for existing detection again to set box
    if (detection && imageContainerRef.current) {
      const displayWidth = img.width;
      const displayHeight = img.height;
      
      console.log("Image display dimensions:", displayWidth, displayHeight);
      console.log("Detection coordinates:", detection.x_min, detection.y_min, detection.x_max, detection.y_max);
      
      // Check for valid coordinates
      if (
        typeof detection.x_min === 'number' && 
        typeof detection.y_min === 'number' && 
        typeof detection.x_max === 'number' && 
        typeof detection.y_max === 'number'
      ) {
        // Calculate box from normalized coordinates (0-1)
        const startX = detection.x_min * displayWidth;
        const startY = detection.y_min * displayHeight;
        const width = (detection.x_max - detection.x_min) * displayWidth;
        const height = (detection.y_max - detection.y_min) * displayHeight;
        
        console.log("Setting box from detection:", { startX, startY, width, height });
        
        // Set the localization box to match the existing detection
        setLocalizationBox({
          startX,
          startY,
          width,
          height
        });
      } else {
        console.warn("Detection has invalid coordinates:", detection);
      }
    } else {
      // If we don't have a detection yet, fetch one from the server
      fetchDetection();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Macroscopic Image for Case: {caseId || "No Case ID"}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={<ArrowBack />}
        onClick={() => navigate(returnPath)}
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        Return to Case Viewer
      </Button>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading image and detection data...
          </Typography>
        </Box>
      ) : !imagePath ? (
        <Box>
          <Typography variant="body1" gutterBottom>
            No image uploaded yet. Upload macroscopic image below:
          </Typography>
          
          <Box 
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              position: 'relative',
              borderRadius: 2,
              border: isDragActive ? 
                `2px dashed ${theme.palette.primary.main}` : 
                `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
              p: 3,
              mb: 2,
              transition: 'all 0.2s ease',
              ...(isDragActive && {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              })
            }}
          >
            {isDragActive && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                zIndex: 1,
                borderRadius: 1,
              }}>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                  Drop image here
                </Typography>
              </Box>
            )}
            
            <Stack spacing={2}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  width: '100%',
                  justifyContent: 'center'
                }}
              >
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<FileUpload />}
                  disabled={uploading}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    transition: 'all 0.2s ease',
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    }
                  }}
                >
                  Choose Image
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  disabled={uploading}
                  onClick={openCamera}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    transition: 'all 0.2s ease',
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.main,
                    '&:hover': {
                      borderColor: theme.palette.secondary.dark,
                      bgcolor: alpha(theme.palette.secondary.main, 0.04),
                    }
                  }}
                >
                  Use Camera
                </Button>
              </Box>
              
              {!selectedFileName && !isDragActive && (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  align="center"
                  sx={{ 
                    opacity: 0.7,
                    fontStyle: 'italic'
                  }}
                >
                  or drag and drop image here
                </Typography>
              )}
              
              {selectedFile && imageUrl && (
                <Fade in={true} timeout={500}>
                  <Card sx={{ maxWidth: 500, mx: 'auto', mt: 2 }}>
                    <CardMedia
                      component="img"
                      height="300"
                      image={imageUrl}
                      alt="Selected image preview"
                      sx={{ objectFit: "contain", backgroundColor: theme.palette.background.paper }}
                    />
                    <CardContent sx={{ pb: 1 }}>
                      <Stack 
                        direction="row" 
                        justifyContent="space-between" 
                        alignItems="center"
                      >
                        <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                          {selectedFileName} ({Math.round(selectedFile.size/1024)} KB)
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={clearSelectedImage}
                          sx={{ color: theme.palette.grey[500] }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                </Fade>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileUpload />}
                  onClick={handleUploadButtonClick}
                  disabled={!selectedFile || uploading}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${alpha(theme.palette.primary.main, 0.8)} 90%)`,
                    transition: 'all 0.3s ease',
                    boxShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.4)}`,
                    '&:hover': {
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.action.disabled, 0.2),
                    }
                  }}
                >
                  {uploading ? 'Uploading...' : 'Upload Image'}
                </Button>
                
                {uploading && (
                  <CircularProgress size={24} sx={{ ml: 2 }} />
                )}
              </Box>
            </Stack>
          </Box>
          
          {uploadSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Image uploaded successfully!
            </Alert>
          )}
          
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </Box>
      ) : (
        <Card sx={{ mb: 3 }}>
          <Box 
            ref={imageContainerRef} 
            sx={{ 
              position: 'relative',
              height: 400,
              backgroundColor: theme.palette.background.paper,
              cursor: isLocalizing ? 'crosshair' : 'default',
              overflow: 'hidden', // Prevent overflow
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={(e) => isDrawing && handleMouseUp(e)}
          >
            <CardMedia
              component="img"
              height="400"
              image={imageUrl || `${API_BASE_URL}${imagePath}`}
              alt="Macroscopic Case Image"
              sx={{ 
                objectFit: "contain", 
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%'
              }}
              onLoad={handleImageLoad}
            />
            
            {/* Drawing overlay */}
            {localizationBox && (
              <Box
                sx={{
                  position: 'absolute',
                  left: Math.min(localizationBox.startX, localizationBox.startX + localizationBox.width),
                  top: Math.min(localizationBox.startY, localizationBox.startY + localizationBox.height),
                  width: Math.abs(localizationBox.width),
                  height: Math.abs(localizationBox.height),
                  border: isLocalizing ? '3px dashed #4caf50' : '3px solid #4caf50',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  pointerEvents: 'none',
                  zIndex: 100,
                  boxShadow: '0 0 5px rgba(0,0,0,0.3)'
                }}
              />
            )}
            
            {/* Localization controls overlay */}
            {isLocalizing && (
              <Paper 
                elevation={3} 
                sx={{ 
                  position: 'absolute', 
                  top: 10, 
                  right: 10, 
                  p: 1, 
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Typography variant="body2">
                  Draw a box around the lesion
                </Typography>
                <Tooltip title="Cancel">
                  <IconButton color="error" onClick={handleCancelLocalizing}>
                    <Cancel />
                  </IconButton>
                </Tooltip>
              </Paper>
            )}
          </Box>
          
          <CardContent>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              {!isLocalizing ? (
                <>
                  <Button 
                    startIcon={editingDetectionId ? <Edit /> : <TouchApp />}
                    variant="contained" 
                    color="success" 
                    onClick={handleStartLocalizing}
                    disabled={loading || isLocalizing}
                  >
                    {editingDetectionId ? "Edit Localization" : "Localize Lesion Manually"}
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={resetImage}
                    disabled={isLocalizing}
                  >
                    Remove Image
                  </Button>
                </>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  Click and drag on the image to select the lesion
                </Typography>
              )}
            </Stack>
            
            {uploadSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <AlertTitle>Success!</AlertTitle>
                {editingDetectionId ? "Localization has been updated successfully." : "Image has been saved successfully."}
              </Alert>
            )}
            
            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {uploadError}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Typography variant="h6">Detection Status</Typography>
      <Box sx={{ mt: 1, mb: 2 }}>
        {!detection ? (
          <Typography color="text.secondary">No lesion localization has been added yet.</Typography>
        ) : detection.label === "manual_detection" ? (
          <Box sx={{ py: 1 }}>
            <Alert severity="success" icon={<Check />} sx={{ mb: 1 }}>
              <AlertTitle>Lesion manually localized</AlertTitle>
              {typeof detection.x_min === 'number' && typeof detection.y_min === 'number' && 
               typeof detection.x_max === 'number' && typeof detection.y_max === 'number' ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Coordinates: ({Math.round(detection.x_min * 100)}%, {Math.round(detection.y_min * 100)}%) to ({Math.round(detection.x_max * 100)}%, {Math.round(detection.y_max * 100)}%)
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Coordinates available but format is invalid
                </Typography>
              )}
              <Typography variant="body2">
                Confidence: {typeof detection.confidence === 'number' ? `${Math.round(detection.confidence * 100)}%` : 'Unknown'}
              </Typography>
            </Alert>
          </Box>
        ) : (
          <Box sx={{ py: 1 }}>
            <Typography>
              Detection available: {detection.label || 'Unknown type'}
              {typeof detection.confidence === 'number' && detection.confidence > 0 && 
                ` (Confidence: ${Math.round(detection.confidence * 100)}%)`}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog for Manual Localization */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>
          {editingDetectionId ? "Update Lesion Localization" : "Save Lesion Localization"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {editingDetectionId 
              ? "Would you like to update this lesion localization or redraw the selection?" 
              : "Would you like to save this lesion localization or redraw the selection?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => {
              setConfirmDialogOpen(false);
              setLocalizationBox(null);
            }}
          >
            Redraw
          </Button>
          <Button 
            variant="contained"
            color="success"
            startIcon={<Check />}
            onClick={handleConfirmLocalization}
          >
            {editingDetectionId ? "Update Localization" : "Save Localization"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog 
        open={cameraOpen} 
        onClose={closeCamera} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: '#000',
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', height: { xs: 400, sm: 500 } }}>
          {cameraLoading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            }}>
              <CircularProgress color="primary" />
              <Typography color="white" sx={{ mt: 2 }}>
                Initializing camera...
              </Typography>
              {cameraInitAttempts > 0 && (
                <Typography color="white" variant="caption" sx={{ mt: 1, maxWidth: '80%', textAlign: 'center' }}>
                  {cameraInitAttempts === 1 
                    ? "First attempt failed. Trying alternate camera..." 
                    : "Still having trouble. Try the Switch Camera button."}
                </Typography>
              )}
            </Box>
          )}
          
          {cameraError && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              p: 3,
            }}>
              <Typography color="error" variant="h6" align="center" gutterBottom>
                Camera Error
              </Typography>
              <Typography color="white" align="center" sx={{ mb: 2 }}>
                {cameraError}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={closeCamera}
                >
                  Close
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={switchCamera}
                >
                  Try Switch Camera
                </Button>
              </Stack>
            </Box>
          )}
          
          <video 
            ref={videoRef}
            autoPlay 
            playsInline
            muted
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',
              backgroundColor: '#000'
            }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {!cameraLoading && !cameraError && availableCameras.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              <Typography variant="caption" color="white">
                Camera {currentCameraIndex + 1}/{availableCameras.length}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'space-between', 
          bgcolor: '#000',
          p: 2
        }}>
          <Button 
            onClick={closeCamera} 
            variant="text"
            startIcon={<Close />}
            sx={{ color: '#fff' }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={switchCamera}
            color="info"
            variant="contained"
            sx={{ color: '#fff' }}
            endIcon={
              availableCameras.length > 1 ? 
                <Typography variant="caption" component="span">
                  ({currentCameraIndex + 1}/{availableCameras.length})
                </Typography> : 
                undefined
            }
          >
            Switch Camera
          </Button>
          
          <Button 
            onClick={captureImage} 
            variant="contained"
            startIcon={<PhotoCamera />}
            color="primary"
            disabled={cameraLoading || !!cameraError}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseDetection;